import React, { useState } from 'react';

import { Flex, Text } from '@chakra-ui/react';
import { Badge, ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faClock
} from '@fortawesome/pro-regular-svg-icons';
import {
  getFormattedDate,
  getFormattedDateRange,
  isDeadlineApproaching
} from 'utils/utils';
import { CardLink } from 'containers/Search/style';
import { Grid } from '@morressier/ts';
import OpenCallsCardSkeletonLoader from 'components/Loader/OpenCallsCardSkeletonLoader';
import { useOpenCalls } from 'clients/internal/platformGateway';

const ITEMS_PER_PAGE = 5; // Number of events to show per page in Review Tasks Table

interface Props {}

const OpenCalls: React.FC<Props> = () => {
  // state
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading } = useOpenCalls(currentPage - 1, ITEMS_PER_PAGE);

  const openCalls = data?.data || [];
  const totalOpenCalls = data?.total || 0;

  const totalPages = Math.ceil(totalOpenCalls / ITEMS_PER_PAGE);
  const isLastPage = currentPage >= totalPages;
  const isFirstPage = currentPage === 1;

  const renderCfXCards = () => {
    return openCalls.map(cfx => {
      const isCfa = cfx.type === 'CFA';
      const href = isCfa
        ? `/call-for-abstracts/${cfx?.eventShortName}`
        : `/call-for-papers/${cfx?._id}`;

      const iDA = isDeadlineApproaching(new Date(cfx.deadline));

      const deadlineBadgeBgColor = iDA
        ? 'errorFillInverse'
        : 'neutralSecondaryFill';
      const deadlineBadgeColor = iDA
        ? 'neutralPrimaryContentInverse'
        : 'neutralPrimaryContent';

      const DeadlineBadge = () => (
        <Badge
          variant="neutral"
          size="context"
          background={deadlineBadgeBgColor}
          textColor={deadlineBadgeColor}
        >
          <Flex alignItems="center" gap="4px">
            <FontAwesomeIcon icon={faClock} />
            Closes {getFormattedDate(new Date(cfx.deadline))}
          </Flex>
        </Badge>
      );

      return (
        <CardLink
          key={cfx._id}
          className="test-CallForAbstractCard"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Flex
            padding="24px"
            gap="8px"
            borderRadius="md"
            direction="column"
            backgroundColor="surfacePrimaryFill"
          >
            <Flex gap="8px">
              <Badge variant="neutral" size="context">
                {cfx.type === 'CFA' ? 'Call for Abstracts' : 'Call for Papers'}
              </Badge>
              <DeadlineBadge />
            </Flex>
            <Flex direction="column" gap="2px" height="76px">
              <Text textStyle="headingItem">{cfx.name}</Text>
              <Text textStyle="textBody">
                {getFormattedDateRange(
                  new Date(cfx.startDate),
                  new Date(cfx.endDate)
                )}
              </Text>
              <Text textStyle="textBody">{cfx.organizationName}</Text>
            </Flex>
          </Flex>
        </CardLink>
      );
    });
  };

  const nextPage = () => {
    if (!isLastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (!isFirstPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div>
      <Flex justifyContent="space-between" marginBottom="8">
        <Text textStyle="headingSectionBrand">Open Calls</Text>
      </Flex>
      <Grid xsmall={1} gap="1rem" style={{ marginBottom: '32px' }}>
        {isLoading ? (
          <>
            <OpenCallsCardSkeletonLoader />
            <OpenCallsCardSkeletonLoader />
            <OpenCallsCardSkeletonLoader />
            <OpenCallsCardSkeletonLoader />
            <OpenCallsCardSkeletonLoader />
          </>
        ) : (
          renderCfXCards()
        )}
      </Grid>
      {totalPages > 1 && (
        <Flex justifyContent="end" marginBottom="8" gap="4">
          <ButtonText
            iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={prevPage}
            disabled={isFirstPage}
          >
            Prev
          </ButtonText>
          <ButtonText
            iconRight={<FontAwesomeIcon icon={faArrowRight} />}
            onClick={nextPage}
            disabled={isLastPage}
          >
            Next
          </ButtonText>
        </Flex>
      )}
    </div>
  );
};

export default OpenCalls;
